import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  CurrencyFlagEmoji,
  DataTable,
  DateUI,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FacetedFilterStandalone,
  Icon,
  PrimaryFullScreenSpinner,
  StatusUI,
  Txt,
  toast,
  UserProfile,
} from "@repo/ui";
import {
  currencyCodeToName,
  formatLocalDateComponents,
  knownLogoUrls,
  prettyPrintEnum,
} from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense, useCallback, useState } from "react";
import { PiBuildingOffice, PiPlus } from "react-icons/pi";
import { RiArrowLeftFill } from "react-icons/ri";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { accountOnboardingRequestStatusValues } from "~/gql/client/enumValues";
import type {
  AdminFxOnboardingRequestFragment,
  AdminMercuryOnboardingRequestFragment,
  AdminProductOnboardingRequestFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/onboarding-requests")({
  head: () => opsHead("Onboarding Requests"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const QUEUE_QUERY = graphql(`
  query AdminAccountOnboardingRequestsQueue {
    accountOnboardingRequestsQueue {
      id
      pendingCount
      requests {
        ...AdminFxOnboardingRequest
        ...AdminProductOnboardingRequest
        ...AdminMercuryOnboardingRequest
      }
    }
  }

  fragment AdminFxOnboardingRequest on FXOnboardingRequest {
    id
    organisation {
      id
      name
      squareLogoUrl
    }
    businessEntity {
      id
      name
    }
    currency
    status
    userProfile {
      id
      ...UserProfileUI
    }
    createdAt
  }

  fragment AdminProductOnboardingRequest on ProductAccountOnboardingRequest {
    id
    organisation {
      id
      name
      squareLogoUrl
    }
    status
    userProfile {
      id
      ...UserProfileUI
    }
    businessEntity {
      id
      name
    }
    product {
      id
      name
    }
    createdAt
  }

  fragment AdminMercuryOnboardingRequest on MercuryOnboardingRequest {
    id
    organisation {
      id
      name
      squareLogoUrl
    }
    status
    userProfile {
      id
      ...UserProfileUI
    }
    businessEntity {
      id
      name
    }
    createdAt
  }
`);

function View() {
  const { data } = useSuspenseQuery(QUEUE_QUERY);
  const requests = data.accountOnboardingRequestsQueue?.requests ?? [];

  return (
    <AdminPageContainer testId="admin-onboarding-requests">
      <AdminPageHeader
        title="Onboarding Requests"
        buttons={
          <Link to="/onboarding-requests/new">
            <Button variant="primary" leftIcon={<PiPlus />}>
              New request
            </Button>
          </Link>
        }
      />
      <DataTable
        data={requests}
        columns={columns}
        pageSize={50}
        className="overflow-hidden border"
        initialState={{ columnFilters: [{ id: "status", value: ["PENDING"] }] }}
        toolbarComponent={({ table }) => (
          <div className="flex items-center gap-2">
            <FacetedFilterStandalone
              column={table.getColumn("status")}
              title="Status"
              options={accountOnboardingRequestStatusValues.map((value) => ({
                label: prettyPrintEnum(value),
                value,
              }))}
            />
          </div>
        )}
      />
    </AdminPageContainer>
  );
}

const columns: ColumnDef<
  | AdminFxOnboardingRequestFragment
  | AdminProductOnboardingRequestFragment
  | AdminMercuryOnboardingRequestFragment
>[] = [
  {
    id: "organisation",
    header: "Client",
    cell: ({ row }) => {
      const item = row.original;
      const businessEntityName = item.businessEntity?.name;
      return (
        <Link to={`/organisations/${item.organisation?.id}`}>
          <div className="flex items-center gap-2">
            <Icon
              size="8"
              imageUrl={item.organisation?.squareLogoUrl ?? undefined}
              fallbackIcon={<PiBuildingOffice />}
            />
            <div className="flex flex-col">
              <Txt variant="name">{item.organisation?.name ?? "-"}</Txt>
              {businessEntityName && (
                <Txt variant="caption">{businessEntityName}</Txt>
              )}
            </div>
          </div>
        </Link>
      );
    },
  },
  {
    id: "product",
    header: "Product",
    cell: ({ row }) => {
      const item = row.original;
      if (item.__typename === "FXOnboardingRequest") {
        return (
          <div className="flex items-center gap-2">
            <CurrencyFlagEmoji
              className="h-6 w-8 rounded-sm text-[24px] shadow-surface saturate-[0.9]"
              currency={item.currency}
            />
            <div className="flex flex-col">
              <Txt variant="name">{currencyCodeToName[item.currency]}</Txt>
              <Txt variant="caption">Primary FX account</Txt>
            </div>
          </div>
        );
      }
      if (item.__typename === "MercuryOnboardingRequest") {
        return (
          <div className="flex items-center gap-2">
            <Icon
              size="8"
              imageUrl={knownLogoUrls.mercury}
              variant="square"
              bordered={false}
            />
            <Txt variant="name">Mercury</Txt>
          </div>
        );
      }
      return (
        <Link to={`/products/${item.product?.id}`} className="hover:underline">
          <Txt variant="name">{item.product?.name ?? "-"}</Txt>
        </Link>
      );
    },
  },
  {
    id: "requestedAt",
    header: "Requested at",
    cell: ({ row }) => {
      const parts = formatLocalDateComponents(row.original.createdAt);
      return (
        <div className="flex flex-col">
          <Txt>{parts.shortDate}</Txt>
          <Txt variant="caption">{parts.shortTime}</Txt>
        </div>
      );
    },
  },
  {
    id: "userProfile",
    header: "Requesting user",
    cell: ({ row }) =>
      row.original.userProfile && <UserProfile {...row.original.userProfile} />,
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: "Status",
    cell: ({ row }) => <StatusUI status={row.original.status} />,
    filterFn: (row, _id, values: string[]) =>
      values.includes(row.original.status),
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const item = row.original;
      const canCancel =
        item.status === "PENDING" || item.status === "USER_REVIEW_REQUIRED";
      if (item.__typename === "MercuryOnboardingRequest") {
        const organisationId = item.organisation?.id;
        const businessEntityId = item.businessEntity?.id;
        return (
          <div className="flex items-center gap-2">
            {organisationId && businessEntityId && (
              <Link
                to={`/organisations/${organisationId}/business-entities/${businessEntityId}/mercury-onboarding`}
              >
                <Button variant="outline">Open form</Button>
              </Link>
            )}
            {canCancel && <CancelMercury {...item} />}
          </div>
        );
      }
      if (!canCancel) {
        return null;
      }
      if (item.__typename === "FXOnboardingRequest") {
        return <CancelFx {...item} />;
      }
      return <CancelProduct {...item} />;
    },
  },
];

const CANCEL_FX_MUTATION = graphql(`
  mutation CancelFXOnboardingRequestFromQueue($input: CancelFXOnboardingRequestsInput!) {
    cancelFXOnboardingRequests(input: $input) {
      accountOnboardingRequestsQueue {
        id
        pendingCount
        requests {
          ...AdminFxOnboardingRequest
          ...AdminProductOnboardingRequest
          ...AdminMercuryOnboardingRequest
        }
      }
    }
  }
`);

const CANCEL_PRODUCT_MUTATION = graphql(`
  mutation CancelProductOnboardingRequestFromQueue($input: CancelProductOnboardingRequestsInput!) {
    cancelProductOnboardingRequests(input: $input) {
      accountOnboardingRequestsQueue {
        id
        pendingCount
        requests {
          ...AdminFxOnboardingRequest
          ...AdminProductOnboardingRequest
          ...AdminMercuryOnboardingRequest
        }
      }
    }
  }
`);

const CANCEL_MERCURY_MUTATION = graphql(`
  mutation CancelMercuryOnboardingRequestFromQueue($input: CancelMercuryOnboardingRequestsInput!) {
    cancelMercuryOnboardingRequests(input: $input) {
      accountOnboardingRequestsQueue {
        id
        pendingCount
        requests {
          ...AdminFxOnboardingRequest
          ...AdminProductOnboardingRequest
          ...AdminMercuryOnboardingRequest
        }
      }
    }
  }
`);

function CancelFx({
  id,
  currency,
  organisation,
  userProfile,
  createdAt,
}: AdminFxOnboardingRequestFragment) {
  const [open, setOpen] = useState(false);
  const [cancelMutation, { loading }] = useMutation(CANCEL_FX_MUTATION);

  const handleClick = useCallback(async () => {
    await cancelMutation({
      variables: { input: { ids: [id] } },
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "FX onboarding request cancelled",
        });
        setOpen(false);
      },
      onError: (error) => {
        const errorMessage =
          error.graphQLErrors?.[0]?.message ||
          "An unexpected error occurred. Please try again later.";
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to cancel FX onboarding request",
          description: errorMessage,
        });
      },
    });
  }, [cancelMutation, id]);

  return (
    <Dialog onOpenChange={setOpen} {...{ open }}>
      <DialogTrigger>
        <Button variant="destructive">Cancel</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm FX onboarding request cancellation</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <span>Currency: {currency}</span>
          <span>Organisation: {organisation?.name}</span>
          <span>
            Requested at: <DateUI date={createdAt} variant="DATETIME" />
          </span>
          {userProfile && (
            <span className="flex items-center gap-2">
              Requested by: <UserProfile {...userProfile} />
            </span>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            leftIcon={<RiArrowLeftFill />}
            variant="outline"
            onClick={(e) => {
              setOpen(false);
              e.preventDefault();
            }}
          >
            Back
          </Button>
          <Button
            variant="destructive"
            disabled={loading}
            loading={loading}
            onClick={handleClick}
          >
            Confirm cancellation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelMercury({
  id,
  organisation,
  userProfile,
  createdAt,
}: AdminMercuryOnboardingRequestFragment) {
  const [open, setOpen] = useState(false);
  const [cancelMutation, { loading }] = useMutation(CANCEL_MERCURY_MUTATION);

  const handleClick = useCallback(async () => {
    await cancelMutation({
      variables: { input: { ids: [id] } },
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Mercury onboarding request cancelled",
        });
        setOpen(false);
      },
      onError: (error) => {
        const errorMessage =
          error.graphQLErrors?.[0]?.message ||
          "An unexpected error occurred. Please try again later.";
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to cancel Mercury onboarding request",
          description: errorMessage,
        });
      },
    });
  }, [cancelMutation, id]);

  return (
    <Dialog onOpenChange={setOpen} {...{ open }}>
      <DialogTrigger>
        <Button variant="destructive">Cancel</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Confirm Mercury onboarding request cancellation
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <span>Product: Mercury</span>
          <span>Organisation: {organisation?.name}</span>
          <span>
            Requested at: <DateUI date={createdAt} variant="DATETIME" />
          </span>
          {userProfile && (
            <span className="flex items-center gap-2">
              Requested by: <UserProfile {...userProfile} />
            </span>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            leftIcon={<RiArrowLeftFill />}
            variant="outline"
            onClick={(e) => {
              setOpen(false);
              e.preventDefault();
            }}
          >
            Back
          </Button>
          <Button
            variant="destructive"
            disabled={loading}
            loading={loading}
            onClick={handleClick}
          >
            Confirm cancellation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelProduct({
  id,
  product,
  organisation,
  userProfile,
  createdAt,
}: AdminProductOnboardingRequestFragment) {
  const [open, setOpen] = useState(false);
  const [cancelMutation, { loading }] = useMutation(CANCEL_PRODUCT_MUTATION);

  const handleClick = useCallback(async () => {
    await cancelMutation({
      variables: { input: { ids: [id] } },
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Product onboarding request cancelled",
        });
        setOpen(false);
      },
      onError: (error) => {
        const errorMessage =
          error.graphQLErrors?.[0]?.message ||
          "An unexpected error occurred. Please try again later.";
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to cancel product onboarding request",
          description: errorMessage,
        });
      },
    });
  }, [cancelMutation, id]);

  return (
    <Dialog onOpenChange={setOpen} {...{ open }}>
      <DialogTrigger>
        <Button variant="destructive">Cancel</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Confirm product onboarding request cancellation
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <span>Product: {product?.name}</span>
          <span>Organisation: {organisation?.name}</span>
          <span>
            Requested at: <DateUI date={createdAt} variant="DATETIME" />
          </span>
          {userProfile && (
            <span className="flex items-center gap-2">
              Requested by: <UserProfile {...userProfile} />
            </span>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            leftIcon={<RiArrowLeftFill />}
            variant="outline"
            onClick={(e) => {
              setOpen(false);
              e.preventDefault();
            }}
          >
            Back
          </Button>
          <Button
            variant="destructive"
            disabled={loading}
            loading={loading}
            onClick={handleClick}
          >
            Confirm cancellation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
